import { API_CONFIG } from '../config/api.config';
import { AuthResponse, CompleteGoogleRegistrationRequest, GoogleRegistration, LoginRequest, RegisterRequest, User, UpdateProfileRequest } from '../types';
import { safeLocalStorage } from '../utils/safeStorage';
import { apiService } from './api.service';

export const authService = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    // Clear any existing auth data first to prevent old session issues
    apiService.clearAuthData();
    this.clearUser();

    const response = await apiService.post<AuthResponse>(
      API_CONFIG.ENDPOINTS.AUTH.REGISTER,
      data
    );

    // Save new auth data
    apiService.saveAuthData(response.data.token, response.data.refreshToken);
    this.saveUser(response.data.user);

    return response.data;
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(
      API_CONFIG.ENDPOINTS.AUTH.LOGIN,
      data
    );

    // Save auth data
    apiService.saveAuthData(response.data.token, response.data.refreshToken);
    this.saveUser(response.data.user);

    return response.data;
  },

  async createGoogleChallenge(): Promise<string> {
    const response = await apiService.post<{ challenge: string }>(
      API_CONFIG.ENDPOINTS.AUTH.GOOGLE_CHALLENGE
    );
    return response.data.challenge;
  },

  async loginWithGoogle(credential: string, challenge: string): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(
      API_CONFIG.ENDPOINTS.AUTH.GOOGLE,
      { credential, challenge }
    );

    apiService.saveAuthData(response.data.token, response.data.refreshToken);
    this.saveUser(response.data.user);
    return response.data;
  },

  async startGoogleRegistration(credential: string, challenge: string): Promise<GoogleRegistration> {
    const response = await apiService.post<GoogleRegistration>(
      API_CONFIG.ENDPOINTS.AUTH.GOOGLE_REGISTRATION_START,
      { credential, challenge }
    );
    return response.data;
  },

  async completeGoogleRegistration(data: CompleteGoogleRegistrationRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(
      API_CONFIG.ENDPOINTS.AUTH.GOOGLE_REGISTRATION_COMPLETE,
      data
    );
    apiService.saveAuthData(response.data.token, response.data.refreshToken);
    this.saveUser(response.data.user);
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await apiService.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      apiService.clearAuthData();
      this.clearUser();
    }
  },

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await apiService.patch<User>(API_CONFIG.ENDPOINTS.USERS.UPDATE, data);
    const updatedUser = response.data;
    this.saveUser(updatedUser);
    return updatedUser;
  },

  async getMe(): Promise<User> {
    const response = await apiService.get<User>(API_CONFIG.ENDPOINTS.USERS.ME);
    this.saveUser(response.data);
    return response.data;
  },

  /**
   * Permanently deletes the current account. The backend expects the current password
   * in the DELETE body (axios needs the `data` option for that). The caller must then
   * call clearSession() (the token is no longer valid, so /auth/logout is not called).
   */
  async deleteAccount(password?: string, reason?: string): Promise<void> {
    // Google-only accounts have no password: the backend then asks for a recent sign-in instead.
    const body: { password?: string; reason?: string } = {};
    if (password) body.password = password;
    const trimmedReason = reason?.trim();
    if (trimmedReason) body.reason = trimmedReason;
    await apiService.delete(API_CONFIG.ENDPOINTS.USERS.ME, { data: body });
  },

  clearSession(): void {
    apiService.clearAuthData();
    this.clearUser();
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.post(
      `${API_CONFIG.ENDPOINTS.AUTH.BASE}/change-password?currentPassword=${encodeURIComponent(currentPassword)}&newPassword=${encodeURIComponent(newPassword)}`
    );
  },

  saveUser(user: User): void {
    safeLocalStorage.setItem('user', JSON.stringify(user));
  },

  getUser(): User | null {
    const userData = safeLocalStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  },

  clearUser(): void {
    safeLocalStorage.removeItem('user');
  },

  isAuthenticated(): boolean {
    return !!safeLocalStorage.getItem('token');
  },

  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.roles.includes(role as User['roles'][number]) || false;
  },

  isProvider(): boolean {
    const user = this.getUser();
    return user?.isProvider || false;
  },
};
